/**
 * @file: ProductRewardHistoryList.tsx
 *
 * Product-commission tab content of the rewards-history screen. One card
 * per product_referral (order + product context) with its fee-item rows
 * (referring / adoption / closing, or their service-advisor share
 * variants) and approval log. Isolated tab content — the host screen
 * switches between this and the campaign-reward list via a segment
 * control.
 */

import { Hr } from '@euler/app/components/Hr';
import { MediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { StackLayout } from '@euler/app/components/StackLayout';
import { TouchCopyable } from '@euler/app/components/TouchCopyable';
import { Typography } from '@euler/app/components/typography';
import { SettingsNavParams } from '@euler/app/flows/settings/screens';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import Euler from '@euler/components/layout';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useInfiniteListWithCursor } from '@euler/functions/useInfiniteListWithCursor';
import { useScreenContentContainerStyle } from '@euler/functions/wrapNavigatorScreen';
import { MediaObject } from '@euler/model';
import { IdType } from '@euler/model/common';
import {
  ApprovalAction,
  ApprovalStatus,
  ProductReferralFeeItemStatus,
  ProductReferralFeeItemType,
} from '@euler/model/enum';
import {
  ProductReferralFeeItemInfo,
  ProductReferralInfo,
} from '@euler/model/product/ProductReferralInfo';
import { useServiceFactory } from '@euler/services/factory';
import { formatMoney } from '@euler/utils/formatMoney';
import { formatDateTime } from '@euler/utils/formatters';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useEffect, useMemo } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kLimit = 5;
const keyExtractor = (tx: ProductReferralInfo) => String(tx.id);

enum ItemStatus {
  Pending = 'pending',
  Rejected = 'rejected',
  Accepted = 'accepted',
  Revoked = 'revoked',
  Resolved = 'resolved',
}

const kStatusInfo: {
  [p in ItemStatus]: { text: string; color: string };
} = {
  [ItemStatus.Pending]: { text: '待审核', color: StatusColors.Warn },
  [ItemStatus.Rejected]: { text: '已拒绝', color: StatusColors.Danger },
  [ItemStatus.Accepted]: { text: '已通过', color: StatusColors.Success },
  [ItemStatus.Revoked]: { text: '已撤销', color: StatusColors.Danger },
  [ItemStatus.Resolved]: { text: '已发放', color: StatusColors.Success },
};

const kApprovalActionInfo: {
  [p in ApprovalAction]: { text: string; color: string };
} = {
  [ApprovalAction.Accept]: { text: '通过', color: StatusColors.Success },
  [ApprovalAction.Reject]: { text: '拒绝', color: StatusColors.Danger },
  [ApprovalAction.Revoke]: { text: '撤销', color: StatusColors.Danger },
};

const kFeeItemTypeInfo: {
  [p in ProductReferralFeeItemType]: { text: string; color: string };
} = {
  [ProductReferralFeeItemType.ReferringFee]: {
    text: '推荐奖励',
    color: StatusColors.Info,
  },
  [ProductReferralFeeItemType.AdoptionFee]: {
    text: '施工奖励',
    color: StatusColors.Notice,
  },
  [ProductReferralFeeItemType.ClosingFee]: {
    text: '成交奖励',
    color: StatusColors.Success,
  },
};

export const ProductRewardHistoryList = ({
  isServiceAgentShare,
}: {
  isServiceAgentShare?: boolean;
}) => {
  const { bottom } = useSafeAreaInsets();
  const { rpx } = useResponsiveContext();
  const { userService } = useServiceFactory();
  const contentContainerStyle = useScreenContentContainerStyle();
  const navigation = useNavigation<StackNavigationProp<SettingsNavParams>>();

  const { data, status, fetch, fetchMore } = useInfiniteListWithCursor<
    ProductReferralInfo,
    IdType
  >({
    limit: kLimit,
    query: async nextKey => {
      return await userService.searchMyProductReferralFeeItems(
        { isServiceAgentShare },
        nextKey,
        kLimit,
      );
    },
  });

  const onViewProductDetail = useCallback(
    (productReferral: ProductReferralInfo) => {
      navigation.push('_productDetail', {
        id: productReferral.productId,
        subTitleColor: 'rgba(255, 255, 255, 0.7)',
      });
    },
    [navigation],
  );

  const renderItem: ListRenderItem<ProductReferralInfo> = useCallback(
    ({ item }) => {
      return (
        <Cell
          productReferral={item}
          isServiceAgentShare={isServiceAgentShare}
          onViewProductDetail={onViewProductDetail}
        />
      );
    },
    [isServiceAgentShare, onViewProductDetail],
  );

  useEffect(() => {
    void fetch();
  }, [fetch]);

  return (
    <FlatList
      data={data}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      onEndReached={() => fetchMore()}
      ItemSeparatorComponent={() => <Hr color="#ddd" height={rpx(1)} />}
      contentContainerStyle={[
        {
          flexGrow: 1,
          padding: rpx(0),
          paddingBottom: bottom,
        },
        contentContainerStyle,
      ]}
      ListHeaderComponent={
        !status.loadedDataLength && status.isLoading ? (
          <Center
            css={`
              flex: 1;
            `}
          >
            <ActivityIndicator />
          </Center>
        ) : null
      }
      ListFooterComponent={
        status.loadedDataLength > 0 && status.isLoading ? (
          <ActivityIndicator
            css={`
              margin-top: ${rpx(10)}px;
            `}
          />
        ) : !status.hasMore ? (
          <Typography.Text
            color={'#999'}
            size={rpx(12)}
            mt={rpx(10)}
            align="center"
          >
            没有更多了
          </Typography.Text>
        ) : null
      }
    />
  );
};

const Cell = memo(
  ({
    productReferral,
    isServiceAgentShare,
    style,
    onViewProductDetail,
  }: {
    productReferral: ProductReferralInfo;
    isServiceAgentShare?: boolean;
    style?: StyleProp<ViewStyle>;
    onViewProductDetail?: (productReferral: ProductReferralInfo) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const infoFontSize = rpx(12);

    const onViewProductDetailPress = useCallback(() => {
      onViewProductDetail?.(productReferral);
    }, [onViewProductDetail, productReferral]);

    const issueMedias = useMemo(
      () =>
        productReferral.jobs
          .flatMap(x =>
            x.procedures
              .filter(p => p.type === 'before')
              .flatMap(p => p.medias),
          )
          .map<MediaObject>(x => ({
            id: x.cid,
            type: x.type.includes('image') ? 'image' : 'video',
            url: x.url,
            coverUrl: x.coverUrl,
          })),
      [productReferral.jobs],
    );

    const constructionMedias = useMemo(
      () =>
        productReferral.jobs
          .flatMap(x =>
            x.procedures
              .filter(p => p.type !== 'before')
              .flatMap(p => p.medias),
          )
          .map<MediaObject>(x => ({
            id: x.cid,
            type: x.type.includes('image') ? 'image' : 'video',
            url: x.url,
            coverUrl: x.coverUrl,
          })),
      [productReferral.jobs],
    );

    const copyText = useMemo(
      () =>
        [
          `订单号码: ${productReferral.orderNo}`,
          `奖励编号: ${productReferral.id}`,
          `车牌号码: ${productReferral.licensePlateNo}`,
          `车型名称: ${productReferral.vehicleName}`,
          `产品名称: [${productReferral.productBrandName}] ${productReferral.productName}`,
          `进厂时间: ${formatDateTime(productReferral.orderCreatedAt)}`,
          `完工时间: ${formatDateTime(productReferral.orderFinishedAt)}`,
          `记录时间: ${formatDateTime(productReferral.createdAt)}`,
          ...productReferral.feeItems.map(
            item =>
              `${kFeeItemTypeInfo[item.type]?.text ?? item.type}: ${formatMoney(
                item.amount,
                { prefix: true },
              )}, ${item.status}, ${item.approvalStatus} (ID: ${item.id})`,
          ),
        ].join('\n'),
      [
        productReferral.createdAt,
        productReferral.feeItems,
        productReferral.id,
        productReferral.licensePlateNo,
        productReferral.orderCreatedAt,
        productReferral.orderFinishedAt,
        productReferral.orderNo,
        productReferral.productBrandName,
        productReferral.productName,
        productReferral.vehicleName,
      ],
    );

    return (
      <StackLayout
        direction="column"
        style={style}
        css={`
          padding: ${rpx(10)}px ${rpx(15)}px;
        `}
      >
        <StackLayout
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={rpx(4)}
          mb={rpx(5)}
        >
          <Icon name="tools" size={rpx(16)} />
          <Typography.Text medium size={rpx(16)} lineHeight={rpx(26)} flex={1}>
            {productReferral.subject}
          </Typography.Text>
          <TouchCopyable text={copyText}>
            <Typography.Text light size={rpx(10)}>
              复制记录信息
            </Typography.Text>
          </TouchCopyable>
        </StackLayout>
        <StackLayout
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={rpx(8)}
        >
          <StackLayout>
            <Typography.Text size={infoFontSize}>
              车牌号码: {productReferral.licensePlateNo}
            </Typography.Text>
            <Typography.Text size={infoFontSize}>
              车型名称: {productReferral.vehicleName}
            </Typography.Text>
            <Text>
              <Typography.Text size={infoFontSize}>
                产品名称:{' '}
                {`[${productReferral.productBrandName}] ${productReferral.productName}`}
              </Typography.Text>
              <Text
                style={{
                  opacity: 0.5,
                  fontStyle: 'italic',
                  fontWeight: 'bold',
                }}
              >{` x${productReferral.qty ?? 1}`}</Text>
            </Text>
            <Typography.Label size={infoFontSize}>
              进厂时间: {formatDateTime(productReferral.orderCreatedAt)}
            </Typography.Label>
            <Typography.Label size={infoFontSize}>
              完工时间: {formatDateTime(productReferral.orderFinishedAt)}
            </Typography.Label>
            <Typography.Label size={infoFontSize}>
              记录时间: {formatDateTime(productReferral.createdAt)}
            </Typography.Label>
          </StackLayout>
          <TouchableOpacity onPress={onViewProductDetailPress}>
            <Img
              uri={productReferral.productCoverUrl}
              css={`
                width: ${rpx(90)}px;
                height: ${rpx(90)}px;
                border-radius: ${rpx(10)}px;
                overflow: hidden;
              `}
            />
          </TouchableOpacity>
        </StackLayout>
        <StackLayout
          direction="column"
          alignItems="stretch"
          divider={<Hr color="#d7d7d7" />}
          gap={rpx(8)}
          css={`
            background-color: #eee;
            padding: ${rpx(10)}px;
            border-radius: ${rpx(5)}px;
          `}
        >
          {productReferral.feeItems.map(item => (
            <FeeItemListItem
              item={item}
              key={item.id}
              isServiceAgentShare={isServiceAgentShare}
            />
          ))}
        </StackLayout>
        {issueMedias.length > 0 && (
          <>
            <Typography.Text color="#777" size={rpx(12)} mt={rpx(5)}>
              故障图片
            </Typography.Text>
            <MediaCarousel
              medias={issueMedias}
              display="list"
              size={rpx(48)}
              itemSpacer={rpx(5)}
              playIconSize={rpx(21)}
              itemStyle={{
                borderRadius: rpx(5),
                marginTop: rpx(5),
                overflow: 'hidden',
              }}
            />
          </>
        )}
        {constructionMedias.length > 0 && (
          <>
            <Typography.Text color="#777" size={rpx(12)} mt={rpx(5)}>
              施工图片
            </Typography.Text>
            <MediaCarousel
              medias={constructionMedias}
              display="list"
              size={rpx(48)}
              itemSpacer={rpx(5)}
              playIconSize={rpx(21)}
              itemStyle={{
                borderRadius: rpx(5),
                marginTop: rpx(5),
                overflow: 'hidden',
              }}
            />
          </>
        )}
      </StackLayout>
    );
  },
);

const FeeItemListItem = memo(
  ({
    item,
    isServiceAgentShare,
    style,
  }: {
    item: ProductReferralFeeItemInfo;
    isServiceAgentShare?: boolean;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={style}>
        <StackLayout direction="row" alignItems="center">
          <Euler.View flex={1}>
            <FeeItemTypeLabel
              type={item.type}
              isServiceAgentShare={isServiceAgentShare}
              textStyle={{ color: '#333' }}
            />
          </Euler.View>
          <Typography.Label size={rpx(12)} lineHeight={rpx(18)} bold flex={1}>
            {formatMoney(item.amount, { prefix: true })}
          </Typography.Label>
          <Euler.View flex={1} flexDirection="row" justifyContent="flex-end">
            <FeeItemStatusLabel item={item} />
          </Euler.View>
        </StackLayout>
        {item.approvalLogs.length > 0 ? (
          <StackLayout
            css={`
              margin-top: ${rpx(5)}px;
            `}
          >
            {item.approvalLogs.map(log => (
              <StackLayout
                key={log.id}
                direction="row"
                gap={rpx(5)}
                alignItems="center"
              >
                <Typography.Label size={rpx(10)} lineHeight={rpx(18)} light>
                  {formatDateTime(log.createdAt)}
                </Typography.Label>
                <ApprovalActionLabel action={log.approvalResult} />
                {log.remark ? (
                  <>
                    <Typography.Text lineHeight={rpx(18)}>
                      {' - '}
                    </Typography.Text>
                    <Typography.Text
                      size={rpx(10)}
                      flex={1}
                      color={
                        log.approvalResult === ApprovalAction.Accept
                          ? StatusColors.Success
                          : StatusColors.Danger
                      }
                    >
                      {`${log.remark}`}
                    </Typography.Text>
                  </>
                ) : null}
              </StackLayout>
            ))}
          </StackLayout>
        ) : null}
      </View>
    );
  },
);

const FeeItemStatusLabel = memo(
  ({
    item,
    style,
  }: {
    style?: StyleProp<ViewStyle>;
    item: ProductReferralFeeItemInfo;
  }) => {
    const { rpx } = useResponsiveContext();
    const status: ItemStatus =
      item.status === ProductReferralFeeItemStatus.Resolved
        ? ItemStatus.Resolved
        : item.approvalStatus === ApprovalStatus.Accepted
        ? ItemStatus.Accepted
        : item.approvalStatus === ApprovalStatus.Rejected
        ? ItemStatus.Rejected
        : item.approvalLogs?.[0]?.approvalResult === ApprovalAction.Revoke
        ? ItemStatus.Revoked
        : ItemStatus.Pending;
    const statusInfo = kStatusInfo[status];
    return (
      <View
        style={style}
        css={`
          background-color: ${statusInfo.color};
          padding: 0 ${rpx(5)}px;
          border-radius: ${rpx(3)}px;
        `}
      >
        <Typography.Label color="#fff" size={rpx(10)} lineHeight={rpx(18)}>
          {statusInfo.text}
        </Typography.Label>
      </View>
    );
  },
);

const FeeItemTypeLabel = memo(
  ({
    type,
    style,
    textStyle,
    isServiceAgentShare,
  }: {
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
    type: ProductReferralFeeItemType;
    isServiceAgentShare?: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={style}>
        <Typography.Label
          color={kFeeItemTypeInfo[type]?.color ?? '#000'}
          size={rpx(12)}
          lineHeight={rpx(18)}
          medium
          style={textStyle}
        >
          {(kFeeItemTypeInfo[type]?.text ?? type) +
            (isServiceAgentShare ? '分成' : '')}
        </Typography.Label>
      </View>
    );
  },
);

const ApprovalActionLabel = memo(
  ({
    action,
    style,
  }: {
    style?: StyleProp<ViewStyle>;
    action: ApprovalAction;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Typography.Label
        color={kApprovalActionInfo[action]?.color ?? '#000'}
        size={rpx(10)}
        lineHeight={rpx(18)}
        style={style}
      >
        {kApprovalActionInfo[action]?.text ?? action}
      </Typography.Label>
    );
  },
);
